import { normalizeLegacyPrinterMaster } from './legacyPrinterMaster.js';
import type { MasterOp, SyncEventInput, SyncPushRequest } from '@trackeat/shared';
import type { PendingOpRow } from '../db/local.js';

const LEGACY_DIRECT_STOCK_KINDS = new Set(['stock_in', 'stock_out', 'stock_reserved']);

/** Preserve FIFO while keeping file-bearing master batches below the Edge body cap. */
export function boundPendingPushBytes(
  pending: readonly PendingOpRow[],
  maxBytes = 4 * 1024 * 1024,
): PendingOpRow[] {
  const batch: PendingOpRow[] = [];
  let bytes = 1024;
  for (const row of pending) {
    const size = new TextEncoder().encode(JSON.stringify(row.payload)).length + 128;
    if (batch.length && bytes + size > maxBytes) break;
    batch.push(row);
    bytes += size;
  }
  return batch;
}

export function isLegacyDirectStockEvent(event: Pick<SyncEventInput, 'kind'>): boolean {
  return LEGACY_DIRECT_STOCK_KINDS.has(event.kind);
}

export function assertFreshEventIsNotDirectStock(event: Pick<SyncEventInput, 'kind'>): void {
  if (isLegacyDirectStockEvent(event)) {
    throw new Error('stock_event_requires_stock_command_v1');
  }
}

/**
 * `pending_ops` predates StockCommandV1 and can survive an app upgrade. Those
 * rows must finish through the server's deliberately retained legacy branch:
 * declaring `stock_command_v1` for that request would make the server reject
 * them as a direct stock write. Fresh stock writes never enter this outbox.
 */
export function buildLegacyPendingPushRequest(pending: readonly PendingOpRow[]): SyncPushRequest {
  const events = pending
    .filter((op) => op.kind === 'event')
    .map((op) => op.payload as SyncEventInput);
  const hasLegacyDirectStock = events.some(isLegacyDirectStockEvent);

  return {
    sync_protocol: 1,
    client_version:
      (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'unknown',
    client_capabilities: hasLegacyDirectStock
      ? ['sync_poison_isolation_v1']
      : ['stock_command_v1', 'sync_poison_isolation_v1'],
    events,
    master_ops: pending.filter((op) => op.kind === 'master').map((op) => normalizeLegacyPrinterMaster(op.payload as MasterOp)),
    stock_commands_v1: [],
  };
}
